import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import { useState } from "react";
import Search from "../../../form/Search";
import Table from "../../../elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ticketDelete,
  ticketList,
  ticketStatusChange,
} from "../../../../services/ticketService";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { organizationList } from "../../../../services/organizationService";
import PopoverDropdown from "../../../elements/PopoverDropdown";
import { useOrganization } from "../../../../context/OrganizationContext";
import Dropdown from "../../../form/Dropdown";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const statusList = [
  { name: "Active", value: "active", type: "checkbox" },
  { name: "In Active", value: "in-active", type: "checkbox" },
];

const eventItems = [
  {
    _id: "1",
    name: "Test1",
    link: "/",
  },
  {
    _id: "2",
    name: "Test2",
    link: "/",
  },
  {
    _id: "3",
    name: "Test3",
    link: "/",
  },
];

// Enhanced styling function for visibility
const getVisibilityStyle = (visibility) => {
  const visibilityLower = visibility?.toLowerCase();
  const visibilityStyles = {
    visible: {
      backgroundColor: "#D1FAE5",
      color: "#047857",
      borderColor: "#10B981",
    },
    hidden: {
      backgroundColor: "#FEE2E2",
      color: "#DC2626",
      borderColor: "#EF4444",
    },
    hiddennotsale: {
      backgroundColor: "#FEF3C7",
      color: "#D97706",
      borderColor: "#F59E0B",
    },
    custom: {
      backgroundColor: "#DBEAFE",
      color: "#1D4ED8",
      borderColor: "#3B82F6",
    },
  };

  return (
    visibilityStyles[visibilityLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Format visibility text for display
const formatVisibilityText = (visibility) => {
  const visibilityMap = {
    visible: "Visible",
    hidden: "Hidden",
    hiddennotsale: "Hidden Not Sale",
    custom: "Custom Schedule",
  };

  return (
    visibilityMap[visibility?.toLowerCase()] ||
    visibility?.charAt(0)?.toUpperCase() + visibility?.slice(1)
  );
};

const TicketsList = () => {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["ticket", filters, search, selectedOrganization],
    queryFn: async () => {
      const res = await ticketList({
        ...filters,
        organization: selectedOrganization,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   ticket status change handler =================>
  // const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
  //   useMutation({
  //     mutationFn: async ({ id, status }) => {
  //       queryClient.setQueryData(
  //         ["ticket", filters, search, selectedOrganization],
  //         (state) => {
  //           if (state) {
  //             let stateData = JSON.parse(JSON.stringify(state?.data));
  //             stateData = (stateData || []).map((elm) =>
  //               elm?._id === id
  //                 ? {
  //                     ...elm,
  //                     status: status,
  //                   }
  //                 : elm
  //             );
  //             return { ...state, data: stateData };
  //           }
  //         }
  //       );
  //       const res = await ticketStatusChange({
  //         ids: [id],
  //         status: status,
  //       });
  //       return { res, payload: { id, status } };
  //     },
  //     onSuccess: ({ res, payload }) => {
  //       if (res?.status === 200) {
  //         toast.success("Status updated successfully!");
  //       } else {
  //         queryClient.setQueryData(
  //           ["ticket", filters, search, selectedOrganization],
  //           (state) => {
  //             if (state) {
  //               let stateData = JSON.parse(JSON.stringify(state?.data));
  //               stateData = (stateData || []).map((elm) =>
  //                 elm?._id === payload?.id
  //                   ? {
  //                       ...elm,
  //                       status: payload?.status,
  //                     }
  //                   : elm
  //               );
  //               return { ...state, data: stateData };
  //             }
  //           }
  //         );
  //         toast.warning(res?.message || "Failed to update status");
  //       }
  //     },
  //     onError: (error) => {
  //       toast.error("Failed to update status");
  //       console.log(error);
  //     },
  //   });

  const tableHeadData = [
    {
      _id: 1,
      name: t("Name"),
      align: "left",
    },
    {
      _id: "event",
      name: t("Event"),
      align: "left",
    },
    {
      _id: "price",
      name: t("Price"),
      align: "left",
    },
    {
      _id: "quantity",
      name: t("Quantity"),
      align: "left",
    },
    {
      _id: "visibility",
      name: t("Visibility"),
      align: "left",
      ...tableFilter({
        filterKey: "visibility",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [
          { name: "Visible", value: "visible", type: "checkbox" },
          { name: "Hidden", value: "hidden", type: "checkbox" },
          {
            name: "Visibility when not on sale",
            value: "hiddennotsale",
            type: "checkbox",
          },
          { name: "Custom schedule", value: "custom", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 3,
      name: t("Created On"),
      align: "left",
    },
    {
      _id: 8,
      name: t("Action"),
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    const visibilityStyle = getVisibilityStyle(item?.visibility);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.name) || "-",
          image: item?.logo?.url,
          isTooltip: false,
        },
        {
          _id: "event",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: "price",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.price
            ? `${item?.eventData?.currency?.symbol || "$"} ${parseFloat(
                item?.price
              ).toFixed(2)}`
            : "Free",
        },
        {
          _id: "quantity",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.quantity === -1 ? "Unlimited" : item?.quantity || "0",
        },
        {
          _id: "visibility",
          type: "text",
          align: "left",
          cellClass: "!min-w-[140px]",
          data: (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 select-none hover:brightness-95 hover:scale-105`}
              style={{
                backgroundColor: visibilityStyle.backgroundColor,
                color: visibilityStyle.color,
                borderColor: visibilityStyle.borderColor,
              }}
              title="Click to cycle through visibility options"
            >
              {formatVisibilityText(item?.visibility)}
            </span>
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        },
        {
          _id: 9,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 9.1,
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                navigate(
                  `/${lang}/user/events/manage-${
                    item?.type === "addons-event" ? "addons" : "ticket"
                  }/${item?.eventData?.uid}/${item?.uid}`
                );
              },
              standout: false,
            },
            {
              _id: 9.2,
              name: t("Delete"),
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  //   ticket delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await ticketDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(
          ["ticket", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.filter(
                (item) => !payload?.includes(item?._id)
              );

              return { ...state, data: stateData };
            }
          }
        );

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "ticket",
            filters,
            search,
            selectedOrganization,
          ]);
        }
        setTimeout(() => {
          queryClient.invalidateQueries([
            "ticket",
            filters,
            search,
            selectedOrganization,
          ]);
        }, 200);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      toast.error("Failed to delete ticket");
      console.log(error);
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-4 md:space-y-8 py-8 px-0 md:px-5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <h2 className="text-xl md:text-2xl text-slate-900 font-bold font-Montserrat uppercase">
            {t("Tickets Overview")}
          </h2>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full">
              <Search
                placeholder={t("Search here")}
                divClasses={"border-slate-300 bg-slate-50"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default TicketsList;
